import { supabase } from "@/integrations/supabase/client";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN;

declare global {
  interface Window {
    Paddle: any;
  }
}

let paddleInitialized = false;

export function getPaddleEnvironment(): "sandbox" | "live" {
  return clientToken?.startsWith("test_") ? "sandbox" : "live";
}

export async function initializePaddle() {
  if (paddleInitialized) return;

  if (!clientToken) {
    throw new Error("VITE_PAYMENTS_CLIENT_TOKEN is not set");
  }

  return new Promise<void>((resolve, reject) => {
    // If script already loaded by previous call, skip injection
    if (window.Paddle?.Initialize) {
      try {
        const environment = clientToken.startsWith("test_") ? "sandbox" : "production";
        window.Paddle.Environment.set(environment);
        window.Paddle.Initialize({
          token: clientToken,
          eventCallback: (data: any) => {
            if (data?.name === "checkout.error" || data?.type === "checkout.error") {
              console.error("[Paddle] checkout.error event:", data);
            } else {
              console.log("[Paddle] event:", data?.name || data?.type, data);
            }
          },
        });
        paddleInitialized = true;
        resolve();
        return;
      } catch (e) {
        reject(e);
        return;
      }
    }

    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.onload = () => {
      try {
        const environment = clientToken.startsWith("test_") ? "sandbox" : "production";
        window.Paddle.Environment.set(environment);
        window.Paddle.Initialize({
          token: clientToken,
          eventCallback: (data: any) => {
            if (data?.name === "checkout.error" || data?.type === "checkout.error") {
              console.error("[Paddle] checkout.error event:", data);
            } else {
              console.log("[Paddle] event:", data?.name || data?.type, data);
            }
          },
        });
        paddleInitialized = true;
        resolve();
      } catch (e) {
        console.error("[Paddle] Init error:", e);
        reject(e);
      }
    };
    script.onerror = (e) => {
      console.error("[Paddle] Script load failed:", e);
      reject(new Error("Failed to load Paddle.js"));
    };
    document.head.appendChild(script);
  });
}

export async function getPaddlePriceId(priceId: string): Promise<string> {
  const environment = getPaddleEnvironment();
  const { data, error } = await supabase.functions.invoke("get-paddle-price", {
    body: { priceId, environment },
  });
  if (error || !data?.paddleId) {
    throw new Error(`Failed to resolve price: ${priceId}`);
  }
  return data.paddleId;
}
